import { useState, useEffect } from 'react';
import { useTranslate } from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Tooltip,
  Skeleton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import QrCodeIcon from '@mui/icons-material/QrCode';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { QRCodeSVG } from 'qrcode.react';
import { ZapyIcon } from '../icons/ZapyIcon';
import { useZapyInstance } from '../../hooks/useZapyInstance';
import { ConfirmationDialog } from '../../resources/tickets/components/dialogs/ConfirmationDialog';
import { ChannelConnectionSettingsDialog } from './ChannelConnectionSettingsDialog';
import type { ZapyInstanceStatus } from '../../services/api.service';

export interface ZapyInstanceManagerProps {
  channelConnectionId: number;
  channelConnectionName?: string;
  onStatusChange?: (status: ZapyInstanceStatus) => void;
}

const STATUS_CONFIG: Record<string, { color: 'success' | 'warning' | 'error' | 'default' | 'info'; Icon: typeof CheckCircleIcon }> = {
  connected: { color: 'success', Icon: CheckCircleIcon },
  pending_qr_code_scan: { color: 'warning', Icon: HourglassEmptyIcon },
  disconnected: { color: 'error', Icon: ErrorIcon },
  not_found: { color: 'error', Icon: ErrorIcon },
  connecting: { color: 'info', Icon: HourglassEmptyIcon },
};

export function ZapyInstanceManager({ channelConnectionId, channelConnectionName, onStatusChange }: ZapyInstanceManagerProps) {
  const translate = useTranslate();
  const { status, loading, actionLoading, fetchStatus, refreshQRCode, restart, logout } = useZapyInstance({
    channelConnectionId,
    onStatusChange,
  });

  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'restart' | 'logout' | null>(null);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  // Auto-open QR dialog when pending, but only if settings dialog is not already open
  useEffect(() => {
    if (status?.status === 'pending_qr_code_scan' && status.qrCode && !settingsDialogOpen) {
      setQrDialogOpen(true);
    }
  }, [status?.status, status?.qrCode, settingsDialogOpen]);

  // Close QR dialog once the connection leaves the pending state
  useEffect(() => {
    if (qrDialogOpen && status?.status && status.status !== 'pending_qr_code_scan') {
      setQrDialogOpen(false);
    }
  }, [qrDialogOpen, status?.status]);

  // Fallback polling while QR dialog is open — closes the modal even if WebSocket event is lost
  useEffect(() => {
    if (!qrDialogOpen) return;

    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, [qrDialogOpen, fetchStatus]);

  const handleConfirm = async () => {
    if (confirmAction === 'restart') await restart();
    else if (confirmAction === 'logout') await logout();
    setConfirmAction(null);
  };

  const displayStatus = status?.status || 'disconnected';
  const statusConfig = STATUS_CONFIG[displayStatus] || STATUS_CONFIG.connecting;
  const StatusIcon = statusConfig.Icon;

  if (loading && !status) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box flex={1}>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" />
            </Box>
          </Box>
          <Skeleton variant="rectangular" height={100} />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <ZapyIcon fontSize="medium" />
              <Typography variant="h6">
                {channelConnectionName || translate('resources.channel.zapy.title')}
              </Typography>
            </Box>
            <Box display="flex" gap={0.5}>
              <Tooltip title={translate('resources.channel.settings.title')}>
                <span>
                  <IconButton onClick={() => setSettingsDialogOpen(true)} size="small">
                    <SettingsIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={translate('ra.action.refresh')}>
                <span>
                  <IconButton onClick={fetchStatus} disabled={loading} size="small">
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Status */}
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <StatusIcon color={statusConfig.color} />
            <Box flex={1}>
              <Typography variant="body2" color="text.secondary">
                {translate('resources.channel.zapy.status')}
              </Typography>
              <Chip
                label={translate(`resources.channel.zapy.statuses.${displayStatus}`)}
                color={statusConfig.color}
                size="small"
              />
            </Box>
          </Box>

          {/* Connected Info */}
          {displayStatus === 'connected' && (
            <Box mb={2}>
              {status.profileName && (
                <Typography variant="body2">
                  <strong>{translate('resources.channel.zapy.profileName')}:</strong> {status.profileName}
                </Typography>
              )}
              {status.ownerJid && (
                <Typography variant="body2">
                  <strong>{translate('resources.channel.zapy.phoneNumber')}:</strong> {status.ownerJid.replace('@s.whatsapp.net', '')}
                </Typography>
              )}
            </Box>
          )}

          {/* Pending QR Scan */}
          {displayStatus === 'pending_qr_code_scan' && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {translate('resources.channel.zapy.scanQRCode')}
              </Alert>
              {status.qrCode && (
                <Box display="flex" justifyContent="center" mb={2} onClick={() => setQrDialogOpen(true)} sx={{ cursor: 'pointer' }}>
                  <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                    <QRCodeSVG value={status.qrCode} size={200} />
                  </Box>
                </Box>
              )}
            </>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Actions */}
          <Box display="flex" gap={1} flexWrap="wrap">
            {displayStatus === 'pending_qr_code_scan' && (
              <Button
                variant="outlined"
                size="small"
                startIcon={actionLoading === 'qr' ? <CircularProgress size={16} /> : <QrCodeIcon />}
                onClick={refreshQRCode}
                disabled={!!actionLoading}
              >
                {translate('resources.channel.zapy.refreshQR')}
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              color="warning"
              startIcon={actionLoading === 'restart' ? <CircularProgress size={16} /> : <RestartAltIcon />}
              onClick={() => setConfirmAction('restart')}
              disabled={!!actionLoading}
            >
              {translate('resources.channel.zapy.restart')}
            </Button>
            {displayStatus === 'connected' && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={actionLoading === 'logout' ? <CircularProgress size={16} /> : <PowerSettingsNewIcon />}
                onClick={() => setConfirmAction('logout')}
                disabled={!!actionLoading}
              >
                {translate('resources.channel.zapy.disconnect')}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{translate('resources.channel.zapy.scanQRTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {translate('resources.channel.zapy.scanQRInstructions')}
          </Typography>
          {status?.qrCode ? (
            <Box display="flex" justifyContent="center">
              <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                <QRCodeSVG value={status.qrCode} size={300} />
              </Box>
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={refreshQRCode} disabled={actionLoading === 'qr'}>
            {translate('resources.channel.zapy.refreshQR')}
          </Button>
          <Button onClick={() => setQrDialogOpen(false)}>{translate('ra.action.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmationDialog
        open={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirm}
        title={
          confirmAction === 'restart'
            ? translate('resources.channel.zapy.confirmRestart')
            : translate('resources.channel.zapy.confirmLogout')
        }
        message={
          confirmAction === 'restart'
            ? translate('resources.channel.zapy.restartWarning')
            : translate('resources.channel.zapy.logoutWarning')
        }
        confirmText={
          confirmAction === 'restart'
            ? translate('resources.channel.zapy.restart')
            : translate('resources.channel.zapy.disconnect')
        }
        isDangerous={confirmAction === 'logout'}
        isLoading={!!actionLoading}
      />

      {/* Settings Dialog */}
      <ChannelConnectionSettingsDialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        channelConnectionId={channelConnectionId}
        channelType="zapy"
      />
    </>
  );
}

export default ZapyInstanceManager;
